import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';

interface LanguageSelectorProps {
  /**
   * Controls the dropdown direction
   * @default 'below'
   */
  dropdownDirection?: 'above' | 'below';
  /**
   * Controls the button width
   * @default 'compact' - Shows abbreviated language code
   * @default 'expanded' - Shows full language names
   */
  variant?: 'compact' | 'expanded';
  /**
   * When true, shows only the flag emoji
   * @default false
   */
  isCollapsed?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  dropdownDirection = 'below',
  variant = 'compact',
  isCollapsed = false,
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', flag: '🇺🇸', name: t('language.english') },
    { code: 'fr', flag: '🇫🇷', name: t('language.french') },
  ];

  const currentLanguage = languages.find((lang) => lang.code === i18n.language);

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`${isCollapsed ? 'flex' : 'inline-flex'} items-center justify-center gap-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors ${
          isCollapsed
            ? 'w-full h-10 p-0'
            : variant === 'expanded'
              ? 'w-auto min-w-[140px] px-3 py-2'
              : 'w-full px-3 py-2'
        }`}
        aria-label={t('language.selectLanguage')}
        aria-expanded={isOpen}
        aria-haspopup="true"
        title={isCollapsed ? t('language.selectLanguage') : ''}
      >
        <span className="text-base">{currentLanguage?.flag}</span>
        {!isCollapsed && (
          <>
            <span
              className={`font-semibold ${variant === 'expanded' ? 'text-sm' : 'text-xs uppercase tracking-wide'}`}
            >
              {variant === 'expanded' ? currentLanguage?.name : i18n.language}
            </span>
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </>
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute z-50 ${dropdownDirection === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'} ${variant === 'expanded' ? 'w-auto min-w-[140px]' : 'w-full'} bg-white border border-gray-200 rounded-md shadow-lg left-0`}
        >
          <div className="py-1">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`flex items-center gap-3 w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                  i18n.language === language.code
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700'
                } ${isCollapsed ? 'justify-center' : ''}`}
                type="button"
              >
                <span className="text-base">{language.flag}</span>
                {!isCollapsed && (
                  <>
                    <span className="flex-1 font-medium">{language.name}</span>
                    {i18n.language === language.code && (
                      <Check className="w-4 h-4 text-blue-600" />
                    )}
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
